/* Cube 3D - header C picture menu controller (snippets/cube-nav.liquid), 2026-10-19.
 *
 * Desktop (>= 990px): the top-row buttons [data-nav-trigger] open the matching sheet [data-nav-panel].
 *   - Mouse: hovering an item opens its sheet after 70 ms (at once when another sheet is already open);
 *     leaving the row and the sheet closes it after 220 ms.
 *   - Click, Enter or Space toggles; a keyboard open (or ArrowDown) moves focus to the selected group tab.
 *   - Group tabs (role=tab, left list) switch on hover (90 ms intent), click, focus and Up/Down/Home/End.
 *   - Escape, a click on the dimmed page, focus leaving the sheet, or scrolling the page closes it.
 * Phone and tablet (< 990px): the menu button [data-nav-toggle] opens the drawer; each top item is an
 *   accordion button [data-nav-acc] (one open at a time). The page behind is scroll-locked and inert;
 *   Escape closes and returns focus to the menu button.
 * Theme editor: selecting a Menu group or Menu card block opens its sheet on that group.
 */
(function () {
  if (customElements.get('cube-nav')) return;

  var DESKTOP = window.matchMedia('(min-width: 990px)');
  var HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  var OPEN_DELAY = 70;
  var CLOSE_DELAY = 220;
  var TAB_DELAY = 90;

  function listen(mql, fn) {
    if (mql.addEventListener) mql.addEventListener('change', fn);
    else mql.addListener(fn);
  }
  function unlisten(mql, fn) {
    if (mql.removeEventListener) mql.removeEventListener('change', fn);
    else mql.removeListener(fn);
  }

  class CubeNav extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.panels = new Map();
      this.querySelectorAll('[data-nav-panel]').forEach(function (p) {
        self.panels.set(p.getAttribute('data-nav-panel'), p);
      });
      this.triggers = new Map();
      document.querySelectorAll('[data-nav-trigger]').forEach(function (t) {
        self.triggers.set(t.getAttribute('data-nav-trigger'), t);
      });
      this.accs = Array.prototype.slice.call(this.querySelectorAll('[data-nav-acc]'));
      this.drawer = this.querySelector('[data-nav-drawer]');
      this.scrim = this.querySelector('[data-nav-scrim]');
      this.toggle = document.querySelector('[data-nav-toggle]');
      this.active = null;
      this.openedAt = 0;
      this.openedBy = '';
      this.openTimer = null;
      this.closeTimer = null;
      this.tabTimer = null;
      this.topFrame = null;

      this.onKey = this.onKey.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onToggle = this.onToggle.bind(this);

      this.bindTriggers();
      this.bindPanels();
      this.bindTabs();
      this.bindPhone();

      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      listen(DESKTOP, this.onModeChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.setTop();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      unlisten(DESKTOP, this.onModeChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
      document.documentElement.classList.remove('cube-nav-locked');
      this.setInert(false);
    }

    /* Sheets and the drawer start right under the sticky header. */
    setTop() {
      var self = this;
      if (this.topFrame) return;
      this.topFrame = requestAnimationFrame(function () {
        self.topFrame = null;
        var wrap = self.closest('.header-wrapper') || document.querySelector('.header-wrapper');
        var bottom = wrap ? Math.max(0, Math.round(wrap.getBoundingClientRect().bottom)) : 0;
        document.documentElement.style.setProperty('--cube-nav-top', bottom + 'px');
      });
    }

    /* ---------- desktop ---------- */
    bindTriggers() {
      var self = this;
      this.triggers.forEach(function (trig, key) {
        trig.addEventListener('mouseenter', function () {
          if (!DESKTOP.matches || !HOVER.matches) return;
          self.cancelClose();
          clearTimeout(self.openTimer);
          self.openTimer = setTimeout(function () { self.open(key, 'hover'); }, self.active ? 0 : OPEN_DELAY);
        });
        trig.addEventListener('mouseleave', function () {
          clearTimeout(self.openTimer);
          if (HOVER.matches) self.scheduleClose();
        });
        trig.addEventListener('click', function (e) {
          if (!DESKTOP.matches) return;
          e.preventDefault();
          var byKeyboard = e.detail === 0;
          if (self.active === key) {
            /* A click right after hover opened the sheet keeps it open. */
            if (!byKeyboard && self.openedBy === 'hover' && Date.now() - self.openedAt < 450) return;
            self.close(byKeyboard);
            return;
          }
          self.open(key, byKeyboard ? 'key' : 'click');
          if (byKeyboard) self.focusTab(self.panels.get(key));
        });
        trig.addEventListener('keydown', function (e) {
          if (!DESKTOP.matches || e.key !== 'ArrowDown') return;
          e.preventDefault();
          self.open(key, 'key');
          self.focusTab(self.panels.get(key));
        });
        trig.addEventListener('focusout', function (e) {
          if (!DESKTOP.matches || self.active !== key || self.openedBy === 'editor') return;
          var to = e.relatedTarget;
          var panel = self.panels.get(key);
          if (!to || (panel && panel.contains(to))) return;
          if (to.hasAttribute && to.hasAttribute('data-nav-trigger')) return;
          self.close();
        });
      });
    }

    bindPanels() {
      var self = this;
      this.panels.forEach(function (panel, key) {
        panel.addEventListener('mouseenter', function () {
          if (DESKTOP.matches) self.cancelClose();
        });
        panel.addEventListener('mouseleave', function () {
          if (DESKTOP.matches && HOVER.matches) self.scheduleClose();
        });
        panel.addEventListener('focusout', function (e) {
          if (!DESKTOP.matches || self.active !== key || self.openedBy === 'editor') return;
          var to = e.relatedTarget;
          if (!to || panel.contains(to) || to === self.triggers.get(key)) return;
          self.close();
        });
      });
      if (this.scrim) {
        this.scrim.addEventListener('click', function () { self.close(); });
      }
    }

    open(key, how) {
      var panel = this.panels.get(key);
      if (!panel || !DESKTOP.matches) return;
      this.cancelClose();
      this.setTop();
      this.panels.forEach(function (p, k) { p.classList.toggle('is-open', k === key); });
      this.triggers.forEach(function (t, k) { t.setAttribute('aria-expanded', k === key ? 'true' : 'false'); });
      this.classList.add('has-open');
      if (this.active !== key || how === 'editor') {
        this.openedAt = Date.now();
        this.openedBy = how;
      }
      this.active = key;
    }

    close(returnFocus) {
      clearTimeout(this.openTimer);
      this.cancelClose();
      var key = this.active;
      this.panels.forEach(function (p) { p.classList.remove('is-open'); });
      this.triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      this.classList.remove('has-open');
      this.active = null;
      this.openedBy = '';
      if (returnFocus && key && this.triggers.get(key)) this.triggers.get(key).focus();
    }

    scheduleClose() {
      var self = this;
      if (this.openedBy === 'editor') return;
      this.cancelClose();
      this.closeTimer = setTimeout(function () { self.close(); }, CLOSE_DELAY);
    }

    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    bindTabs() {
      var self = this;
      this.querySelectorAll('[role="tablist"]').forEach(function (list) {
        var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
        tabs.forEach(function (tab, i) {
          tab.addEventListener('click', function () { self.selectTab(tab); });
          tab.addEventListener('focus', function () { self.selectTab(tab); });
          tab.addEventListener('mouseenter', function () {
            if (!HOVER.matches) return;
            clearTimeout(self.tabTimer);
            self.tabTimer = setTimeout(function () { self.selectTab(tab); }, TAB_DELAY);
          });
          tab.addEventListener('mouseleave', function () { clearTimeout(self.tabTimer); });
          tab.addEventListener('keydown', function (e) {
            var next = null;
            if (e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
            else if (e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
            else if (e.key === 'Home') next = tabs[0];
            else if (e.key === 'End') next = tabs[tabs.length - 1];
            if (next) {
              e.preventDefault();
              next.focus();
            }
          });
        });
      });
    }

    selectTab(tab) {
      var list = tab.closest('[role="tablist"]');
      if (!list) return;
      list.querySelectorAll('[role="tab"]').forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var group = document.getElementById(t.getAttribute('aria-controls'));
        if (!group) return;
        if (on) group.removeAttribute('data-inactive');
        else group.setAttribute('data-inactive', '');
      });
    }

    focusTab(panel) {
      if (!panel) return;
      var target = panel.querySelector('[role="tab"][aria-selected="true"]') || panel.querySelector('a[href], button');
      if (target) target.focus({ preventScroll: true });
    }

    /* ---------- phone ---------- */
    bindPhone() {
      var self = this;
      if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
      this.accs.forEach(function (acc) {
        acc.addEventListener('click', function () {
          var expand = acc.getAttribute('aria-expanded') !== 'true';
          self.accs.forEach(function (a) {
            var on = expand && a === acc;
            a.setAttribute('aria-expanded', on ? 'true' : 'false');
            var p = self.panels.get(a.getAttribute('data-nav-acc'));
            if (p) p.classList.toggle('is-expanded', on);
          });
          if (expand && self.drawer) {
            var top = acc.getBoundingClientRect().top - self.drawer.getBoundingClientRect().top + self.drawer.scrollTop;
            self.drawer.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          }
        });
      });
    }

    onToggle() {
      if (this.isDrawerOpen()) this.closeDrawer(true);
      else this.openDrawer();
    }

    isDrawerOpen() {
      return !!(this.drawer && this.drawer.classList.contains('is-open'));
    }

    openDrawer() {
      if (!this.drawer || DESKTOP.matches) return;
      var wrap = this.closest('.header-wrapper') || document.querySelector('.header-wrapper');
      if (wrap) {
        document.documentElement.style.setProperty('--cube-nav-top', Math.max(0, Math.round(wrap.getBoundingClientRect().bottom)) + 'px');
      }
      this.drawer.classList.add('is-open');
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('cube-nav-locked');
      this.setInert(true);
      var first = this.drawer.querySelector('.cube-nav__acc');
      if (first) first.focus({ preventScroll: true });
    }

    closeDrawer(returnFocus) {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('cube-nav-locked');
      this.setInert(false);
      if (returnFocus && this.toggle) this.toggle.focus();
    }

    setInert(on) {
      document.querySelectorAll('#MainContent, .shopify-section-group-footer-group').forEach(function (el) {
        if (on) el.setAttribute('inert', '');
        else el.removeAttribute('inert');
      });
    }

    /* ---------- global ---------- */
    onKey(e) {
      if (e.key !== 'Escape') return;
      if (this.isDrawerOpen()) {
        this.closeDrawer(true);
        return;
      }
      if (this.active) {
        e.preventDefault();
        this.close(true);
      }
    }

    onDocClick(e) {
      if (!this.active || this.openedBy === 'editor') return;
      if (e.target.closest && e.target.closest('[data-nav-panel], [data-nav-trigger]')) return;
      this.close();
    }

    onScroll() {
      if (!this.active) return;
      var panel = this.panels.get(this.active);
      if (this.openedBy === 'editor' || (panel && panel.matches(':hover'))) {
        this.setTop();
        return;
      }
      this.close();
    }

    onResize() {
      this.setTop();
    }

    onModeChange() {
      this.close();
      this.closeDrawer(false);
      var self = this;
      this.accs.forEach(function (a) {
        a.setAttribute('aria-expanded', 'false');
        var p = self.panels.get(a.getAttribute('data-nav-acc'));
        if (p) p.classList.remove('is-expanded');
      });
    }

    onBlockSelect(e) {
      var id = e.detail && e.detail.blockId;
      if (!id) return;
      var el = document.getElementById('CubeNavGroup-' + id) || this.querySelector('[data-shopify-editor-block*="' + id + '"]');
      if (!el) return;
      var panel = el.closest('[data-nav-panel]');
      if (!panel) return;
      var key = panel.getAttribute('data-nav-panel');
      var group = el.closest('.cube-nav__group');
      if (DESKTOP.matches) {
        this.open(key, 'editor');
        if (group) {
          var tab = this.querySelector('[role="tab"][aria-controls="' + group.id + '"]');
          if (tab) this.selectTab(tab);
        }
      } else {
        if (!this.isDrawerOpen()) this.openDrawer();
        var acc = null;
        this.accs.forEach(function (a) { if (a.getAttribute('data-nav-acc') === key) acc = a; });
        if (acc && acc.getAttribute('aria-expanded') !== 'true') acc.click();
      }
    }
  }

  customElements.define('cube-nav', CubeNav);
})();
